import { type AiSummaryFieldModalProps } from '@databases/database-ai-fields';
import type { AiSummaryRunResult } from '@databases/database-ai-fields/run-ai-summary-field';
import { createContext, useContext } from 'react';

type AiSummaryFieldConfigModalProps = Omit<
  AiSummaryFieldModalProps,
  'onClose' | 'onGeneratePreview' | 'onSaveAndRun'
> & {
  fieldId: string;
  fieldName: string;
  openedFrom: 'addFieldContextualMenu' | 'editFieldDropdown';
  onClose?: () => void;
  onSaveAndRun?: (
    config: Parameters<NonNullable<AiSummaryFieldModalProps['onSaveAndRun']>>[0],
    abortSignal: AbortSignal
  ) => Promise<AiSummaryRunResult>;
};

export type DatabaseAiConfigModal = {
  type: 'ai-summary-field';
  props: AiSummaryFieldConfigModalProps;
};

type AiSummaryIndicatorRunStart =
  | { kind: 'started'; completion: Promise<AiSummaryRunResult> }
  | { kind: 'blocked' };

interface DatabaseAiConfigModalContextValue {
  openAiConfigModal: (modal: DatabaseAiConfigModal) => void;
  closeAiConfigModal: () => void;
  isAiSummaryRunActive: boolean;
  runAiSummaryWithIndicator: (options: {
    fieldId: string;
    run: (abortSignal: AbortSignal) => Promise<AiSummaryRunResult>;
  }) => AiSummaryIndicatorRunStart;
}

export const DatabaseAiConfigModalContext =
  createContext<DatabaseAiConfigModalContextValue | null>(null);

export const useDatabaseAiConfigModal = () => {
  const value = useContext(DatabaseAiConfigModalContext);

  if (!value) {
    throw new Error(
      'useDatabaseAiConfigModal must be used within DatabaseAiConfigModalProvider'
    );
  }

  return value;
};
